import { useCallback, useEffect, useRef, useState } from 'react'
import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/solid'
import { lmsApi } from '@/api'

interface TrackedVideoPlayerProps {
  lessonId: number
  src: string
  poster?: string
  initialPosition: number
  initialWatched: number
  completed: boolean
  onCompleted: () => void
}

const PING_INTERVAL_MS = 10000
// Avanzamento massimo tra due eventi timeupdate durante la riproduzione normale
const MAX_PLAYBACK_STEP_SECONDS = 1.5
// Scarto oltre il quale il player si riallinea alla posizione registrata dal server
const MAX_SERVER_LAG_SECONDS = 30

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Player per i video con visione obbligatoria: non permette di saltare in
 * avanti oltre il punto già visto e comunica l'avanzamento al server, che
 * completa la lezione quando il video è stato visto fino alla fine.
 */
export default function TrackedVideoPlayer({
  lessonId,
  src,
  poster,
  initialPosition,
  initialWatched,
  completed,
  onCompleted,
}: TrackedVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const watchedRef = useRef(initialWatched)
  const completedRef = useRef(completed)
  const lastPingAtRef = useRef(0)
  const sendingRef = useRef(false)
  const pendingRef = useRef(false)

  const [watched, setWatched] = useState(initialWatched)
  const [duration, setDuration] = useState(0)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    completedRef.current = completed
  }, [completed])

  const sendProgress = useCallback(async () => {
    const video = videoRef.current
    if (!video || completedRef.current || !video.duration) return

    if (sendingRef.current) {
      pendingRef.current = true
      return
    }

    sendingRef.current = true
    lastPingAtRef.current = Date.now()
    const position = Math.min(video.currentTime, watchedRef.current)
    try {
      const result = await lmsApi.updateVideoProgress(lessonId, position, video.duration)

      if (result.is_completed) {
        completedRef.current = true
        setNotice(null)
        onCompleted()
      } else if (video.ended || result.watched_seconds < position - MAX_SERVER_LAG_SECONDS) {
        // Il server non ha registrato tutta la visione (es. connessione assente)
        watchedRef.current = result.watched_seconds
        setWatched(result.watched_seconds)
        video.currentTime = result.watched_seconds
        setNotice(
          `La visione risulta registrata fino al minuto ${formatTime(result.watched_seconds)}: ` +
          'riprendi da questo punto per completare il video.'
        )
      }
    } catch (error) {
      console.error('Error updating video progress:', error)
    } finally {
      sendingRef.current = false
      if (pendingRef.current) {
        pendingRef.current = false
        sendProgress()
      }
    }
  }, [lessonId, onCompleted])

  // Salva la posizione quando si lascia la lezione
  useEffect(() => {
    const video = videoRef.current
    return () => {
      if (video && !completedRef.current && video.duration) {
        lmsApi
          .updateVideoProgress(lessonId, Math.min(video.currentTime, watchedRef.current), video.duration)
          .catch(() => undefined)
      }
    }
  }, [lessonId])

  const handleLoadedMetadata = () => {
    const video = videoRef.current
    if (!video) return
    setDuration(video.duration)

    const resumeAt = Math.min(initialPosition, watchedRef.current)
    if (!completedRef.current && resumeAt > 0 && resumeAt < video.duration - 1) {
      video.currentTime = resumeAt
    }
  }

  const handleTimeUpdate = () => {
    const video = videoRef.current
    if (!video || completedRef.current || video.seeking) return

    const current = video.currentTime
    if (current > watchedRef.current + MAX_PLAYBACK_STEP_SECONDS) {
      video.currentTime = watchedRef.current
      return
    }
    if (current > watchedRef.current) {
      watchedRef.current = current
      setWatched(current)
    }
    if (!video.paused && Date.now() - lastPingAtRef.current >= PING_INTERVAL_MS) {
      sendProgress()
    }
  }

  const handleSeeking = () => {
    const video = videoRef.current
    if (!video || completedRef.current) return
    if (video.currentTime > watchedRef.current + 0.25) {
      video.currentTime = watchedRef.current
      setNotice('Non puoi saltare in avanti: il video deve essere visto fino alla fine.')
    }
  }

  const handleRateChange = () => {
    const video = videoRef.current
    if (!video || completedRef.current) return
    if (video.playbackRate > 1) {
      video.playbackRate = 1
    }
  }

  const percent = completed
    ? 100
    : duration > 0
    ? Math.min(100, Math.floor((watched / duration) * 100))
    : 0

  return (
    <div>
      <div className="aspect-video bg-black rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          controls
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          playsInline
          className="w-full h-full"
          onContextMenu={(e) => e.preventDefault()}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onRateChange={handleRateChange}
          onPlay={() => {
            setNotice(null)
            sendProgress()
          }}
          onPause={sendProgress}
          onEnded={sendProgress}
        >
          Il tuo browser non supporta la riproduzione video.
        </video>
      </div>

      <div className="mt-4 px-1">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="flex items-center font-medium text-gray-700">
            {completed ? (
              <CheckCircleIcon className="h-5 w-5 mr-1 text-green-500" />
            ) : (
              <LockClosedIcon className="h-4 w-4 mr-1 text-amber-500" />
            )}
            {completed ? 'Video visto fino alla fine' : 'Visione completa obbligatoria'}
          </span>
          <span className="text-gray-500">{percent}% visto</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${completed ? 'bg-green-500' : 'bg-primary-600'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        {!completed && (
          <p className="text-xs text-gray-500 mt-2">
            Guarda il video fino alla fine per completare la lezione. Puoi tornare indietro,
            ma non saltare in avanti oltre il punto già visto.
          </p>
        )}
        {notice && !completed && (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2">{notice}</p>
        )}
      </div>
    </div>
  )
}
